import { ChartTimeframe } from '@/types/generated/common/types'

/**
 * The chart grid: every bar width the chart backend serves (`ChartTimeframe`, whose value IS
 * the width in seconds), and the one place each is mapped to the names the UI uses for it.
 *
 * Bars align like the server's: SEC_1..DAY_3 on the epoch, WEEK_1 on ISO Monday, MONTH_1 on
 * the 1st of each UTC calendar month. MONTH_1's 2592000 (30 days) is a label only — a monthly
 * bar runs 28–31 days, so bar math goes through `chartBarStartMs` / `nextChartBarStartMs`,
 * never `timeframe * 1000`.
 */
export interface ChartTimeframeInfo {
  timeframe: ChartTimeframe
  /** TradingView resolution string. */
  resolution: string
  /** Short interval code: Percept's interval code and the TV legend label. */
  code: string
  /** Picker label, also the `trade.chartToolbar.timeframes.*` message key. */
  label: string
}

export const CHART_TIMEFRAMES: readonly ChartTimeframeInfo[] = [
  { timeframe: ChartTimeframe.CHART_TIMEFRAME_SEC_1, resolution: '1S', code: '1s', label: '1s' },
  {
    timeframe: ChartTimeframe.CHART_TIMEFRAME_SEC_15,
    resolution: '15S',
    code: '15s',
    label: '15s',
  },
  { timeframe: ChartTimeframe.CHART_TIMEFRAME_MIN_1, resolution: '1', code: '1m', label: '1m' },
  { timeframe: ChartTimeframe.CHART_TIMEFRAME_MIN_5, resolution: '5', code: '5m', label: '5m' },
  { timeframe: ChartTimeframe.CHART_TIMEFRAME_MIN_15, resolution: '15', code: '15m', label: '15m' },
  { timeframe: ChartTimeframe.CHART_TIMEFRAME_MIN_30, resolution: '30', code: '30m', label: '30m' },
  { timeframe: ChartTimeframe.CHART_TIMEFRAME_HOUR_1, resolution: '60', code: '1h', label: '1H' },
  { timeframe: ChartTimeframe.CHART_TIMEFRAME_HOUR_2, resolution: '120', code: '2h', label: '2H' },
  { timeframe: ChartTimeframe.CHART_TIMEFRAME_HOUR_4, resolution: '240', code: '4h', label: '4H' },
  { timeframe: ChartTimeframe.CHART_TIMEFRAME_HOUR_8, resolution: '480', code: '8h', label: '8H' },
  {
    timeframe: ChartTimeframe.CHART_TIMEFRAME_HOUR_12,
    resolution: '720',
    code: '12h',
    label: '12H',
  },
  { timeframe: ChartTimeframe.CHART_TIMEFRAME_DAY_1, resolution: '1D', code: '1d', label: '1D' },
  { timeframe: ChartTimeframe.CHART_TIMEFRAME_DAY_3, resolution: '3D', code: '3d', label: '3D' },
  { timeframe: ChartTimeframe.CHART_TIMEFRAME_WEEK_1, resolution: '1W', code: '1w', label: '1W' },
  { timeframe: ChartTimeframe.CHART_TIMEFRAME_MONTH_1, resolution: '1M', code: '1M', label: '1M' },
]

/**
 * Tick: the 1s grid drawn as one continuous line that keeps extending as prices arrive. A
 * display mode, not a bar width — the data is SEC_1, so this value never reaches the
 * backend's ChartTimeframe; it lives only in tab/widget config and the pickers. Chart widgets
 * fetch with `chartDataTimeframe` and switch their drawing with `isTickTimeframe`.
 */
export const CHART_TICK_TIMEFRAME = -1
/** TradingView's one-tick resolution, which stands for Tick in TV's interval menu. */
export const CHART_TICK_RESOLUTION = '1T'

export function isTickTimeframe(timeframe: number | undefined): boolean {
  return timeframe === CHART_TICK_TIMEFRAME
}

/** The bar width to fetch and stream for a picker timeframe: Tick reads the 1s bars. */
export function chartDataTimeframe(timeframe: number): number {
  return isTickTimeframe(timeframe) ? ChartTimeframe.CHART_TIMEFRAME_SEC_1 : timeframe
}

const BY_TIMEFRAME = new Map<number, ChartTimeframeInfo>(
  CHART_TIMEFRAMES.map((t) => [t.timeframe, t])
)
const BY_RESOLUTION = new Map(CHART_TIMEFRAMES.map((t) => [t.resolution, t]))
const BY_CODE = new Map(CHART_TIMEFRAMES.map((t) => [t.code, t]))

/** The grid entry for a width in seconds, or undefined when the server would reject it. */
export function getChartTimeframeInfo(seconds: number): ChartTimeframeInfo | undefined {
  return BY_TIMEFRAME.get(seconds)
}

/** TradingView resolution → ChartTimeframe. TV also echoes the bare 'D' / 'W' / 'M' forms. */
export function resolutionToChartTimeframe(resolution: string): ChartTimeframe | undefined {
  const normalized = /^[DWM]$/.test(resolution) ? `1${resolution}` : resolution
  return BY_RESOLUTION.get(normalized)?.timeframe
}

export function chartTimeframeToResolution(timeframe: number): string | undefined {
  return BY_TIMEFRAME.get(timeframe)?.resolution
}

/** Percept interval code ('2h', '1M', …) → ChartTimeframe. */
export function intervalCodeToChartTimeframe(code: string): ChartTimeframe | undefined {
  return BY_CODE.get(code)?.timeframe
}

const DAY_MS = 86_400_000
const WEEK_MS = 7 * DAY_MS
// Epoch day 0 (1970-01-01) is a Thursday; weekly bars start on ISO Monday.
const MONDAY_ANCHOR_MS = 4 * DAY_MS

/** Start (ms) of the bar of width `timeframeSeconds` holding `timeMs`, on the server's grid. */
export function chartBarStartMs(timeMs: number, timeframeSeconds: number): number {
  if (timeframeSeconds === ChartTimeframe.CHART_TIMEFRAME_MONTH_1) {
    const date = new Date(timeMs)
    return Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), 1)
  }
  const widthMs = Math.max(1, timeframeSeconds) * 1000
  const anchorMs = widthMs === WEEK_MS ? MONDAY_ANCHOR_MS : 0
  return Math.floor((timeMs - anchorMs) / widthMs) * widthMs + anchorMs
}

/** `chartBarStartMs` in unix seconds. */
export function chartBarStartSec(timeSec: number, timeframeSeconds: number): number {
  return chartBarStartMs(timeSec * 1000, timeframeSeconds) / 1000
}

/** Start (ms) of the bar `count` bars (negative: earlier) from the one starting at `barStartMs`. */
export function addChartBarsMs(
  barStartMs: number,
  count: number,
  timeframeSeconds: number
): number {
  if (timeframeSeconds === ChartTimeframe.CHART_TIMEFRAME_MONTH_1) {
    const date = new Date(barStartMs)
    return Date.UTC(date.getUTCFullYear(), date.getUTCMonth() + count, 1)
  }
  return barStartMs + count * Math.max(1, timeframeSeconds) * 1000
}

/** Start (ms) of the bar after the one starting at `barStartMs`. */
export function nextChartBarStartMs(barStartMs: number, timeframeSeconds: number): number {
  return addChartBarsMs(barStartMs, 1, timeframeSeconds)
}

/**
 * `timeMs` as a position in bars on the grid (fractional within a bar). The difference of two
 * positions is how many bars apart they sit, which for MONTH_1 is not their gap / 30 days.
 */
export function chartBarPosition(timeMs: number, timeframeSeconds: number): number {
  if (timeframeSeconds === ChartTimeframe.CHART_TIMEFRAME_MONTH_1) {
    const startMs = chartBarStartMs(timeMs, timeframeSeconds)
    const date = new Date(startMs)
    const month = (date.getUTCFullYear() - 1970) * 12 + date.getUTCMonth()
    return month + (timeMs - startMs) / (nextChartBarStartMs(startMs, timeframeSeconds) - startMs)
  }
  return timeMs / (Math.max(1, timeframeSeconds) * 1000)
}
